/**
 * Sélecteur de coupures (billets et pièces) avec boutons − / +, partagé par le retrait et le versement.
 * Styles : withdrawal/etapes.css (classes et-coupure, et-stepper…).
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';
import { DENOMINATIONS } from '../../../shared/constants/denominations';
import { formatMoney } from '../../../shared/utils/formatters';
import '../withdrawal/etapes.css';

interface SelecteurCoupuresProps {
  coupures: Map<number, number>;
  /** Saisie directe d'un nombre */
  onChange: (valeur: number, nombre: number) => void;
  /** Boutons − / + : variation appliquée à la valeur courante (sûr en cas de clics rapides) */
  onDelta: (valeur: number, delta: number) => void;
  /** Affiche les pièces dès l'ouverture (sinon repliées, sauf si déjà utilisées) */
  piecesOuvertes?: boolean;
  disabled?: boolean;
}

export function SelecteurCoupures({ coupures, onChange, onDelta, piecesOuvertes = false, disabled = false }: SelecteurCoupuresProps) {
  const [afficherPieces, setAfficherPieces] = useState(piecesOuvertes);
  const lignes = DENOMINATIONS.filter((d) => d.type === 'bill' || afficherPieces || (coupures.get(d.value) ?? 0) > 0);

  return (
    <div className="et-coupures">
      {lignes.map((d) => {
        const nombre = coupures.get(d.value) ?? 0;
        return (
          <div key={d.value} className={`et-coupure ${nombre > 0 ? 'active' : ''}`}>
            <span className="lib">
              {d.label}
              <small>{d.type === 'bill' ? 'billet' : 'pièce'}</small>
            </span>
            <span className="et-stepper">
              <button
                type="button"
                aria-label={`Retirer un ${d.label}`}
                disabled={disabled || nombre === 0}
                onClick={() => onDelta(d.value, -1)}
              >
                <Minus />
              </button>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                aria-label={`Nombre de ${d.label}`}
                value={nombre}
                disabled={disabled}
                onFocus={(e) => e.target.select()}
                onChange={(e) => onChange(d.value, Math.max(0, parseInt(e.target.value, 10) || 0))}
              />
              <button
                type="button"
                aria-label={`Ajouter un ${d.label}`}
                disabled={disabled}
                onClick={() => onDelta(d.value, 1)}
              >
                <Plus />
              </button>
            </span>
            <span className="tot">{formatMoney(d.value * nombre)}</span>
          </div>
        );
      })}
      <button type="button" className="et-bascule" onClick={() => setAfficherPieces((v) => !v)}>
        {afficherPieces ? <ChevronUp /> : <ChevronDown />}
        {afficherPieces ? 'Masquer les pièces' : 'Afficher les pièces'}
      </button>
    </div>
  );
}
